import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { getUserToken } from '_utils/localData'
import { parseJwt } from '_utils/function'

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Name is required')
        .trim()
        .min(1, 'Name is required'),
    mobile: Yup.string()
        .required('Mobile number is required')
        .trim()
        .min(8, 'Mobile number must be at least 8 characters'),
    email: Yup.string()
        .required('Email is required')
        .email('Please enter a valid email address')
        .trim(),
    noteToHost: Yup.string(),
    createAccount: Yup.boolean(),
    rememberInfo: Yup.boolean(),
})

const getDefaultsFromToken = () => {
    try {
        const token = getUserToken()
        if (token) {
            const decoded = parseJwt(token)
            return {
                name: decoded.name || '',
                mobile: decoded.phone || '',
                email: decoded.email || '',
            }
        }
    } catch (err) {
        // token invalid or expired, ignore
    }
    return { name: '', mobile: '', email: '' }
}

const UserInfoForm = ({ onFormChange, onLoginClick }) => {
    const tokenDefaults = getDefaultsFromToken()

    const {
        register,
        watch,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            name: tokenDefaults.name,
            mobile: tokenDefaults.mobile,
            email: tokenDefaults.email,
            noteToHost: '',
            createAccount: false,
            rememberInfo: false,
        },
    })

    const formValues = watch()

    useEffect(() => {
        if (onFormChange) {
            onFormChange(formValues)
        }
    }, [
        formValues.name,
        formValues.mobile,
        formValues.email,
        formValues.noteToHost,
        formValues.createAccount,
        formValues.rememberInfo,
    ])

    return (
        <div className='user-info-form'>
            <div className='user-info-form__header'>
                <span>Already have a co-bee account? </span>
                <button
                    type='button'
                    className='user-info-form__login-link'
                    onClick={onLoginClick}
                >
                    Log in
                </button>
            </div>

            <div className='user-info-form__field'>
                <label className='user-info-form__label'>Name <span style={{ color: 'red' }}>*</span></label>
                <input
                    type='text'
                    className={`user-info-form__input${errors.name ? ' user-info-form__input--error' : ''}`}
                    {...register('name')}
                />
                {errors.name && <span className='user-info-form__error'>{errors.name.message}</span>}
            </div>

            <div className='user-info-form__field'>
                <label className='user-info-form__label'>Mobile <span style={{ color: 'red' }}>*</span></label>
                <input
                    type='text'
                    className={`user-info-form__input${errors.mobile ? ' user-info-form__input--error' : ''}`}
                    {...register('mobile')}
                />
                {errors.mobile && <span className='user-info-form__error'>{errors.mobile.message}</span>}
            </div>

            <div className='user-info-form__field'>
                <label className='user-info-form__label'>E-mail <span style={{ color: 'red' }}>*</span></label>
                <input
                    type='email'
                    className={`user-info-form__input${errors.email ? ' user-info-form__input--error' : ''}`}
                    {...register('email')}
                />
                {errors.email && <span className='user-info-form__error'>{errors.email.message}</span>}
            </div>

            <div className='user-info-form__field'>
                <label className='user-info-form__label'>Note to Host</label>
                <textarea
                    className='user-info-form__input user-info-form__textarea'
                    {...register('noteToHost')}
                    rows={3}
                />
            </div>

            <div className='user-info-form__checkbox-group'>
                <label className='user-info-form__checkbox-label'>
                    <input
                        type='checkbox'
                        className='user-info-form__checkbox'
                        {...register('createAccount')}
                    />
                    <span className='user-info-form__checkbox-custom' />
                    <span className='user-info-form__checkbox-text'>
                        Create a Co-bee account to track and manage your orders
                    </span>
                </label>
            </div>

            <div className='user-info-form__checkbox-group'>
                <label className='user-info-form__checkbox-label'>
                    <input
                        type='checkbox'
                        className='user-info-form__checkbox'
                        {...register('rememberInfo')}
                    />
                    <span className='user-info-form__checkbox-custom' />
                    <span className='user-info-form__checkbox-text'>
                        Remember name and mobile on this device
                    </span>
                </label>
            </div>
        </div>
    )
}

UserInfoForm.propTypes = {
    onFormChange: PropTypes.func,
    onLoginClick: PropTypes.func,
}

export default UserInfoForm
